/**
 * Accessibility preset definitions.
 *
 * NDIS person-centred design notes (in-code reference for implementers):
 *
 * - **large-touch**: Supports participants with fine-motor difficulty or tremor.
 *   WCAG 2.2 target size (minimum 44×44 CSS px) is exceeded to 48px for comfort.
 * - **high-contrast**: Helps low-vision users and bright outdoor environments.
 *   Uses near-black on near-white with a saturated focus indicator.
 * - **reduced-motion**: Vestibular / cognitive — disables non-essential animation
 *   and respects `prefers-reduced-motion` when the preset is active.
 * - **simplified-nav**: Cognitive load reduction — hides Groups and Announcements
 *   from primary navigation so fewer decisions appear on every screen.
 * - **cognitive-clarity**: Clearer visual hierarchy (larger line-height, narrower
 *   content column) and optional voice-first hint copy on primary actions.
 */

import type { AccessibilityCssVars, AccessibilityPresetId, AccessibilitySettings } from './types'
import { DEFAULT_ACCESSIBILITY_SETTINGS } from './types'

export interface AccessibilityPresetDefinition {
  id: AccessibilityPresetId
  label: string
  description: string
  cssVars: AccessibilityCssVars
  /** When true, primary nav shows only sessions / direct / calls. */
  simplifiedNav: boolean
  reducedMotion: boolean
  highContrast: boolean
  voiceHints: boolean
}

const BASE_VARS: AccessibilityCssVars = {
  '--a11y-font-scale': '1',
  '--a11y-touch-min': '44px',
  '--a11y-line-height': '1.5',
  '--a11y-content-max-width': '80rem',
  '--a11y-focus-ring-width': '2px',
  '--a11y-nav-density': '1',
}

export const ACCESSIBILITY_PRESETS: readonly AccessibilityPresetDefinition[] = [
  {
    id: 'default',
    label: 'Standard',
    description: 'Default layout and spacing for most users.',
    cssVars: BASE_VARS,
    simplifiedNav: false,
    reducedMotion: false,
    highContrast: false,
    voiceHints: false,
  },
  {
    id: 'large-touch',
    label: 'Large touch targets',
    description: 'Bigger buttons and text for easier tapping.',
    cssVars: {
      ...BASE_VARS,
      '--a11y-font-scale': '1.125',
      '--a11y-touch-min': '48px',
      '--a11y-line-height': '1.6',
      '--a11y-focus-ring-width': '3px',
    },
    simplifiedNav: false,
    reducedMotion: false,
    highContrast: false,
    voiceHints: false,
  },
  {
    id: 'high-contrast',
    label: 'High contrast',
    description: 'Stronger colours and focus outlines for low vision.',
    cssVars: {
      ...BASE_VARS,
      '--a11y-focus-ring-width': '4px',
    },
    simplifiedNav: false,
    reducedMotion: false,
    highContrast: true,
    voiceHints: false,
  },
  {
    id: 'reduced-motion',
    label: 'Reduced motion',
    description: 'Minimises animation and decorative movement.',
    cssVars: BASE_VARS,
    simplifiedNav: false,
    reducedMotion: true,
    highContrast: false,
    voiceHints: false,
  },
  {
    id: 'simplified-nav',
    label: 'Simplified navigation',
    description: 'Fewer tabs — Sessions, Direct, and Calls only.',
    cssVars: {
      ...BASE_VARS,
      '--a11y-font-scale': '1.0625',
      '--a11y-line-height': '1.55',
      '--a11y-nav-density': '0.85',
    },
    simplifiedNav: true,
    reducedMotion: false,
    highContrast: false,
    voiceHints: false,
  },
  {
    id: 'cognitive-clarity',
    label: 'Clear & calm',
    description: 'More spacing, narrower content, and voice-friendly hints.',
    cssVars: {
      ...BASE_VARS,
      '--a11y-font-scale': '1.0625',
      '--a11y-line-height': '1.7',
      '--a11y-content-max-width': '48rem',
      '--a11y-touch-min': '48px',
    },
    simplifiedNav: false,
    reducedMotion: true,
    highContrast: false,
    voiceHints: true,
  },
] as const

export function getPresetDefinition(id: AccessibilityPresetId): AccessibilityPresetDefinition {
  return ACCESSIBILITY_PRESETS.find((p) => p.id === id) ?? ACCESSIBILITY_PRESETS[0]
}

/** Merge stored settings with optional overrides into an effective preset id + flags. */
export function resolveAccessibility(settings?: AccessibilitySettings | null): {
  preset: AccessibilityPresetDefinition
  cssVars: AccessibilityCssVars
  simplifiedNav: boolean
  reducedMotion: boolean
  highContrast: boolean
  voiceHints: boolean
} {
  const merged: AccessibilitySettings = {
    ...DEFAULT_ACCESSIBILITY_SETTINGS,
    ...settings,
  }
  const preset = getPresetDefinition(merged.preset)
  return {
    preset,
    cssVars: preset.cssVars,
    simplifiedNav: merged.simplifiedNav ?? preset.simplifiedNav,
    reducedMotion: merged.reducedMotion ?? preset.reducedMotion,
    highContrast: merged.highContrast ?? preset.highContrast,
    voiceHints: merged.voiceHints ?? preset.voiceHints,
  }
}

/** Primary nav module ids visible when simplified nav is active. */
export const SIMPLIFIED_NAV_MODULES = [
  'sessions',
  'messages',
  'forum',
  'calls',
  'notifications',
  'menu',
] as const

export function isNavModuleVisible(
  moduleId: string,
  simplifiedNav: boolean,
): boolean {
  if (!simplifiedNav) return true
  return (SIMPLIFIED_NAV_MODULES as readonly string[]).includes(moduleId)
}
